import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Send, Heart, Check, CheckCheck, Reply, X, ImagePlus, AlertCircle, RotateCw, Clock, Mic, Trash2, Play, Pause, Eye, EyeOff, Pencil, Image as ImageIcon, CalendarHeart } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useChatThread } from '../../hooks/useChatThread'
import { useChatPresence } from '../../hooks/useChatPresence'
import { useCoupleExtras } from '../../hooks/useCoupleExtras'
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder'
import { useUserDirectory } from '../../hooks/useUserDirectory'
import RewriteButton from '../../components/RewriteButton'
import { useAuth } from '../../contexts/AuthContext'
import { uploadMessageImage } from '../../lib/messageImageUpload'
import { uploadMessageAudio } from '../../lib/messageAudioUpload'
import Modal from '../../components/ui/Modal'
import ImageLightbox from '../../components/ImageLightbox'
import { cn } from '../../lib/utils'

const QUICK_EMOJIS = ['❤️', '😂', '👍', '😮', '😢', '🔥']
const MOOD_CHOICES = ['😀', '🥰', '😎', '😴', '😢', '😡', '🤒', '🥳', '🤔', '😶']

// Couple chat thread. WhatsApp-style: partner bubbles on the start
// side, mine on the end side, compose pinned above the bottom tab
// bar. Realtime via useChatThread keeps it live; read receipts ride
// on read_at.
export function MessagesPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const { user } = useAuth()
  const { partnerId: routePartnerId } = useParams()
  const { messages, reactionsByMessage, loading, sending, send, react, resendPush, consumeViewOnce, editMessage, unsendMessage, markRead, userId, partnerId } = useChatThread(routePartnerId)
  const { partnerOnline, partnerTyping, setTyping } = useChatPresence(partnerId)
  const couple = useCoupleExtras(partnerId)
  const recorder = useVoiceRecorder()
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [coupleOpen, setCoupleOpen] = useState(false)
  const [anniversaryInput, setAnniversaryInput] = useState('')

  // Permanent photos exchanged in this thread (excludes view-once,
  // deleted, expired) — newest first for the gallery grid.
  const galleryImages = useMemo(
    () => messages.filter((m) => m.image_url && !m.image_view_limit && !m.deleted_at && !m.image_expired).map((m) => m.image_url).reverse(),
    [messages],
  )

  const [draft, setDraft] = useState('')
  const [replyTo, setReplyTo] = useState(null)
  const [actionRow, setActionRow] = useState(null)
  const [uploading, setUploading] = useState(false)
  // Ephemeral-image mode for the next photo: 0 = normal, 1 = once, 2 = twice.
  const [imageMode, setImageMode] = useState(0)
  const [editRow, setEditRow] = useState(null)
  const [editText, setEditText] = useState('')
  const [lightbox, setLightbox] = useState(null)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [viewportH, setViewportH] = useState(null)
  const fileRef = useRef(null)
  const bottomRef = useRef(null)
  const rootRef = useRef(null)
  const lastCountRef = useRef(0)
  const initialScrolledRef = useRef(false)

  const { resolve } = useUserDirectory()
  const partnerName = partnerId ? resolve(partnerId, locale).name : ''

  // Keep the thread height locked to the *visible* viewport so the
  // composer hugs the keyboard instead of floating above a dead gap.
  // interactive-widget=resizes-content handles this on Chrome/Android;
  // iOS Safari ignores it, so we drive the height off VisualViewport:
  // available = (visible bottom) − (our top in viewport coords).
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return undefined
    let maxVH = 0
    const measure = () => {
      const root = rootRef.current
      if (!root) return
      maxVH = Math.max(maxVH, vv.height)
      // Only override the CSS height while the keyboard is open (the
      // visual viewport drops well below its peak). When it's closed
      // we leave viewportH null so the exact CSS calc applies — that
      // fits the content area with no overflow and no dead space.
      const keyboardOpen = vv.height < maxVH - 80
      if (!keyboardOpen) { setViewportH(null); return }
      const top = root.getBoundingClientRect().top
      setViewportH(Math.max(160, vv.height - top))
    }
    measure()
    vv.addEventListener('resize', measure)
    vv.addEventListener('scroll', measure)
    return () => {
      vv.removeEventListener('resize', measure)
      vv.removeEventListener('scroll', measure)
    }
  }, [])

  // Quick id→row lookup so a bubble can render the message it quotes.
  const byId = useMemo(() => {
    const m = {}
    for (const row of messages) m[row.id] = row
    return m
  }, [messages])

  // Reset the one-time initial scroll when switching conversations.
  useEffect(() => { initialScrolledRef.current = false }, [partnerId])

  // First time the thread has content, jump straight to the latest
  // message (instant — smooth-on-mount doesn't reliably land at the
  // bottom before layout settles). After that, only auto-scroll when
  // the count grows (a send or an inbound realtime row), smoothly.
  useLayoutEffect(() => {
    if (loading || messages.length === 0) return
    const grew = messages.length > lastCountRef.current
    lastCountRef.current = messages.length
    if (!initialScrolledRef.current) {
      initialScrolledRef.current = true
      bottomRef.current?.scrollIntoView({ block: 'end' })
    } else if (grew) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [messages, loading, partnerId])

  // Mark inbound unread read whenever the thread updates while we're
  // looking at it.
  useEffect(() => {
    if (!loading) markRead()
  }, [loading, messages, markRead])

  const handleSend = async () => {
    const text = draft.trim()
    if (!text || sending) return
    setDraft('')
    setTyping(false)
    const replyToId = replyTo?.id || null
    setReplyTo(null)
    await send({ body: text, replyToId })
  }

  const handleThinkingOfYou = async () => {
    if (sending) return
    await send({ body: '❤️' })
  }

  const handleReact = async (emoji) => {
    if (!actionRow) return
    const id = actionRow.id
    setActionRow(null)
    await react(id, emoji)
  }

  const handleReplyTo = () => {
    if (!actionRow) return
    setReplyTo(actionRow)
    setActionRow(null)
  }

  const handleStartEdit = () => {
    if (!actionRow) return
    setEditRow(actionRow)
    setEditText(actionRow.body || '')
    setActionRow(null)
  }

  const handleSaveEdit = async () => {
    if (!editRow) return
    const text = editText.trim()
    const id = editRow.id
    setEditRow(null)
    if (text) await editMessage(id, text)
  }

  const handleUnsend = async () => {
    if (!actionRow) return
    const id = actionRow.id
    setActionRow(null)
    await unsendMessage(id)
  }

  const handleScheduleSend = async () => {
    const text = draft.trim()
    if (!text || !scheduleAt) return
    const when = new Date(scheduleAt)
    if (when.getTime() <= Date.now()) return
    const replyToId = replyTo?.id || null
    setDraft('')
    setReplyTo(null)
    setScheduleOpen(false)
    setScheduleAt('')
    await send({ body: text, replyToId, scheduledFor: scheduleAt })
  }

  const handleStartRecording = () => { recorder.start() }

  const handleCancelRecording = () => { recorder.cancel() }

  const handleStopAndSend = async () => {
    const blob = await recorder.stop()
    if (!blob || !user) return
    setUploading(true)
    const { url } = await uploadMessageAudio(user.id, blob)
    setUploading(false)
    if (!url) return
    const replyToId = replyTo?.id || null
    setReplyTo(null)
    await send({ audioUrl: url, replyToId })
  }

  const handlePickImage = () => fileRef.current?.click()

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user || uploading || sending) return
    setUploading(true)
    const { url } = await uploadMessageImage(user.id, file)
    setUploading(false)
    if (!url) return
    const replyToId = replyTo?.id || null
    const caption = draft.trim()
    const viewLimit = imageMode || null
    setDraft('')
    setReplyTo(null)
    setImageMode(0)
    await send({ body: caption, imageUrl: url, replyToId, imageViewLimit: viewLimit })
  }

  // Open an image. For an ephemeral one the recipient receives, fetch
  // the bytes into a blob first (so it survives the server-side delete)
  // then count the view; normal images open straight from the URL.
  const handleViewImage = async (row) => {
    if (!row.image_url) return
    const ephemeralInbound = row.image_view_limit && row.recipient_id === userId
    if (ephemeralInbound) {
      try {
        const res = await fetch(row.image_url)
        const blob = await res.blob()
        const objUrl = URL.createObjectURL(blob)
        setLightbox({ images: [objUrl], index: 0 })
        consumeViewOnce(row.id)
      } catch {
        setLightbox({ images: [row.image_url], index: 0 })
        consumeViewOnce(row.id)
      }
    } else {
      setLightbox({ images: [row.image_url], index: 0 })
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Group consecutive bubbles by calendar day for the date dividers.
  const grouped = useMemo(() => groupByDay(messages, locale), [messages, locale])

  return (
    // Fixed-height flex column: header + scrollable message region +
    // pinned composer footer. Height is the shell's content area
    // (viewport minus the mobile top inset/breadcrumb + bottom safe
    // area) so the composer always sits at the true bottom even when
    // there are only a couple of messages. The tab bar is hidden on
    // this route, so the composer owns the bottom edge.
    <div
      ref={rootRef}
      className="flex flex-col content-fade"
      style={{ height: viewportH != null
        ? `${viewportH}px`
        : 'calc(100dvh - env(safe-area-inset-top) - 3.5rem - env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate('/messages')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-foreground truncate leading-tight" dir="auto">
            {partnerName || t('messages.list.title')}
          </h1>
          {partnerTyping ? (
            <p className="text-[11px] text-app-primary-500 truncate" dir="auto">{t('messages.thread.typing')}</p>
          ) : partnerOnline ? (
            <p className="text-[11px] text-emerald-500 inline-flex items-center gap-1 truncate" dir="auto">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t('messages.thread.online')}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setGalleryOpen(true)}
          aria-label={t('messages.gallery.title')}
          className="p-2 rounded-md hover:bg-accent text-muted-foreground flex-shrink-0"
        >
          <ImageIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Couple strip — days-together + both moods, tap to set yours. */}
      {partnerId && (
        <button
          type="button"
          onClick={() => { setAnniversaryInput(couple.anniversary || ''); setCoupleOpen(true) }}
          className="flex-shrink-0 mb-2 w-full flex items-center justify-center gap-3 py-1.5 rounded-xl bg-muted/50 text-[12px]"
          dir="auto"
        >
          {couple.daysTogether != null && (
            <span className="inline-flex items-center gap-1 text-app-primary-500 font-semibold">
              <CalendarHeart className="w-3.5 h-3.5" /> {t('messages.couple.daysN', { n: couple.daysTogether })}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <span className="text-base leading-none">{couple.myMood || '🙂'}</span>
            <span className="opacity-50">·</span>
            <span className="text-base leading-none">{couple.partnerMood || '—'}</span>
          </span>
        </button>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="h-full flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground text-center px-8" dir="auto">
              {t('messages.thread.empty', { name: partnerName })}
            </p>
          </div>
        )}

        {!loading && messages.length > 0 && (
          <div className="space-y-1 pb-2">
            {grouped.map((item) => (
              item.type === 'divider' ? (
                <div key={item.key} className="flex justify-center py-2">
                  <span className="text-[11px] text-muted-foreground bg-muted/60 rounded-full px-3 py-0.5" dir="auto">
                    {item.label}
                  </span>
                </div>
              ) : (
                <Bubble
                  key={item.row.id}
                  row={item.row}
                  mine={item.row.sender_id === userId}
                  parent={item.row.reply_to_id ? byId[item.row.reply_to_id] : null}
                  reactions={reactionsByMessage[item.row.id] || []}
                  locale={locale}
                  userId={userId}
                  onTap={() => setActionRow(item.row)}
                  onViewImage={() => handleViewImage(item.row)}
                  onResend={() => resendPush(item.row.id)}
                  t={t}
                />
              )
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Composer — plain flex footer pinned to the bottom of the
          fixed-height column. */}
      <div
        className="flex-shrink-0 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 bg-background border-t border-border/60"
      >
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 ps-2 pe-1 py-1.5 rounded-lg bg-muted/60 border-s-2 border-app-primary-500">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-app-primary-500" dir="auto">
                {t('messages.thread.replyingTo', { name: replyTo.sender_id === userId ? t('messages.thread.you') : partnerName })}
              </p>
              <p className="text-[12px] text-muted-foreground truncate" dir="auto">
                {replyTo.body || (replyTo.image_url ? '📷' : '🎤')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              aria-label={t('settings.close')}
              className="p-1 rounded-md hover:bg-accent text-muted-foreground flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleImageFile}
          className="hidden"
        />
        {recorder.recording ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancelRecording}
              aria-label={t('settings.close')}
              className="p-2 rounded-full text-red-500 hover:bg-red-500/10 flex-shrink-0"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-2xl bg-muted">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-sm text-foreground tabular-nums" dir="ltr">
                {formatDuration(recorder.seconds)}
              </span>
              <span className="text-[11px] text-muted-foreground" dir="auto">
                {t('messages.thread.recording')}
              </span>
            </div>
            <button
              type="button"
              onClick={handleStopAndSend}
              disabled={uploading}
              aria-label={t('messages.gate.close')}
              className="p-2.5 rounded-full bg-app-primary-500 text-white disabled:opacity-50 flex-shrink-0"
            >
              {uploading
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : <Send className="w-5 h-5 rtl:-scale-x-100" />}
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handlePickImage}
              disabled={uploading || sending || !partnerId}
              aria-label={t('messages.thread.attachImage')}
              className="p-2 rounded-full text-app-primary-500 hover:bg-app-primary-500/10 disabled:opacity-50 flex-shrink-0"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            </button>
            {/* Ephemeral-image mode: tap to cycle normal → once → twice. */}
            <button
              type="button"
              onClick={() => setImageMode((m) => (m + 1) % 3)}
              disabled={!partnerId}
              aria-label={t('messages.thread.viewOnceMode')}
              className={cn(
                'relative p-2 rounded-full flex-shrink-0 disabled:opacity-50',
                imageMode > 0 ? 'text-app-primary-500 bg-app-primary-500/10' : 'text-muted-foreground hover:bg-accent',
              )}
            >
              {imageMode > 0 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              {imageMode > 0 && (
                <span className="absolute -top-0.5 -end-0.5 w-4 h-4 rounded-full bg-app-primary-500 text-white text-[9px] font-bold inline-flex items-center justify-center">{imageMode}</span>
              )}
            </button>
            <button
              type="button"
              onClick={handleThinkingOfYou}
              disabled={sending || !partnerId}
              aria-label={t('messages.thread.thinkingOfYou')}
              className="p-2 rounded-full text-app-primary-500 hover:bg-app-primary-500/10 disabled:opacity-50 flex-shrink-0"
            >
              <Heart className="w-5 h-5" />
            </button>
            {draft.trim() && (
              <button
                type="button"
                onClick={() => setScheduleOpen(true)}
                disabled={sending || !partnerId}
                aria-label={t('messages.thread.schedule')}
                className="p-2 rounded-full text-app-primary-500 hover:bg-app-primary-500/10 disabled:opacity-50 flex-shrink-0"
              >
                <Clock className="w-5 h-5" />
              </button>
            )}
            {draft.trim() && (
              <RewriteButton value={draft} onRewritten={setDraft} className="flex-shrink-0" />
            )}
            <textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setTyping(e.target.value.length > 0) }}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={t('messages.thread.placeholder')}
              className="flex-1 max-h-32 px-3 py-2 rounded-2xl border border-input bg-background text-foreground text-sm focus:outline-none resize-none"
            />
            {draft.trim() ? (
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || !partnerId}
                aria-label={t('messages.gate.close')}
                className="p-2.5 rounded-full bg-app-primary-500 text-white disabled:opacity-50 flex-shrink-0"
              >
                {sending
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <Send className="w-5 h-5 rtl:-scale-x-100" />}
              </button>
            ) : (
              recorder.supported && (
                <button
                  type="button"
                  onClick={handleStartRecording}
                  disabled={sending || uploading || !partnerId}
                  aria-label={t('messages.thread.recordVoice')}
                  className="p-2.5 rounded-full bg-app-primary-500 text-white disabled:opacity-50 flex-shrink-0"
                >
                  <Mic className="w-5 h-5" />
                </button>
              )
            )}
          </div>
        )}
        {recorder.error === 'denied' && (
          <p className="text-[11px] text-red-500 mt-1" dir="auto">{t('messages.thread.micDenied')}</p>
        )}
      </div>

      {/* Per-bubble action sheet — modern slide-up with a reaction
          row + actions (Instagram/WhatsApp style). */}
      <MessageActions
        row={actionRow}
        mine={actionRow?.sender_id === userId}
        onReact={handleReact}
        onReply={handleReplyTo}
        onEdit={handleStartEdit}
        onUnsend={handleUnsend}
        onClose={() => setActionRow(null)}
        t={t}
      />

      {/* Edit-message dialog. */}
      <Modal open={!!editRow} onClose={() => setEditRow(null)} ariaLabel={t('messages.thread.edit')}>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">{t('messages.thread.edit')}</h2>
          <button type="button" onClick={() => setEditRow(null)} aria-label={t('settings.close')} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none resize-y mb-3"
          dir="auto"
        />
        <button
          type="button"
          onClick={handleSaveEdit}
          disabled={!editText.trim()}
          className="w-full py-2.5 rounded-lg bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
          dir="auto"
        >
          {t('common.save')}
        </button>
      </Modal>

      {/* Schedule-send picker. */}
      <Modal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        ariaLabel={t('messages.thread.schedule')}
      >
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-app-primary-500 flex-shrink-0" />
          <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
            {t('messages.thread.scheduleTitle')}
          </h2>
        </div>
        <p className="text-[12px] text-muted-foreground mb-2 truncate" dir="auto">
          {draft.trim()}
        </p>
        <input
          type="datetime-local"
          value={scheduleAt}
          onChange={(e) => setScheduleAt(e.target.value)}
          className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none mb-4"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setScheduleOpen(false)}
            className="flex-1 py-2 rounded-md border-2 border-border text-sm text-foreground hover:bg-accent"
            dir="auto"
          >
            {t('settings.close')}
          </button>
          <button
            type="button"
            onClick={handleScheduleSend}
            disabled={!scheduleAt || new Date(scheduleAt).getTime() <= Date.now()}
            className="flex-1 py-2 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
            dir="auto"
          >
            {t('messages.thread.scheduleConfirm')}
          </button>
        </div>
      </Modal>

      {/* Shared photo gallery. */}
      <Modal open={galleryOpen} onClose={() => setGalleryOpen(false)} ariaLabel={t('messages.gallery.title')}>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">{t('messages.gallery.title')}</h2>
          <button type="button" onClick={() => setGalleryOpen(false)} aria-label={t('settings.close')} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        {galleryImages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" dir="auto">{t('messages.gallery.empty')}</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5 max-h-[60vh] overflow-y-auto">
            {galleryImages.map((url, i) => (
              <img
                key={`${url}-${i}`}
                src={url}
                alt=""
                loading="lazy"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() => { setGalleryOpen(false); setLightbox({ images: galleryImages, index: i }) }}
                className="aspect-square w-full object-cover rounded-md cursor-zoom-in select-none [-webkit-touch-callout:none]"
              />
            ))}
          </div>
        )}
      </Modal>

      {/* Couple sheet — set your mood + the anniversary date. */}
      <Modal open={coupleOpen} onClose={() => setCoupleOpen(false)} ariaLabel={t('messages.couple.title')}>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">{t('messages.couple.title')}</h2>
          <button type="button" onClick={() => setCoupleOpen(false)} aria-label={t('settings.close')} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5" dir="auto">{t('messages.couple.yourMood')}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {MOOD_CHOICES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => couple.setMood(m)}
              className={cn('w-10 h-10 rounded-lg text-xl flex items-center justify-center', couple.myMood === m ? 'bg-app-primary-500/20 ring-2 ring-app-primary-500' : 'bg-muted')}
            >
              {m}
            </button>
          ))}
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5" dir="auto">{t('messages.couple.anniversary')}</p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={anniversaryInput}
            onChange={(e) => setAnniversaryInput(e.target.value)}
            dir="ltr"
            className="flex-1 h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none"
          />
          <button
            type="button"
            onClick={() => { if (anniversaryInput) couple.setAnniversaryDate(anniversaryInput) }}
            disabled={!anniversaryInput}
            className="px-4 h-10 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
            dir="auto"
          >
            {t('common.save')}
          </button>
        </div>
      </Modal>

      <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
    </div>
  )
}

// Modern bottom-sheet message menu: a floating reaction row on top
// of a slide-up panel of actions. Replaces the old centered dialog.
function MessageActions({ row, mine, onReact, onReply, onEdit, onUnsend, onClose, t }) {
  if (typeof document === 'undefined' || !row) return null
  const canEdit = mine && row.body && !row.image_url && !row.audio_url && !row.deleted_at
  const isDeleted = !!row.deleted_at
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative mx-auto w-full max-w-screen-sm px-3 pb-3 animate-[slideUp_180ms_ease-out]"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
      >
        {/* Reaction row */}
        {!isDeleted && (
          <div className="mb-2 mx-auto w-fit flex items-center gap-1 rounded-full bg-card border border-border shadow-lg px-2 py-1.5">
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReact(emoji)}
                className="text-2xl px-1.5 py-0.5 rounded-full hover:bg-accent active:scale-90 transition-transform"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        {/* Action list */}
        <div className="rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
          {!isDeleted && (
            <ActionRow icon={Reply} label={t('messages.thread.reply')} onClick={onReply} />
          )}
          {canEdit && (
            <ActionRow icon={Pencil} label={t('messages.thread.edit')} onClick={onEdit} />
          )}
          {mine && !isDeleted && (
            <ActionRow icon={Trash2} label={t('messages.thread.unsend')} onClick={onUnsend} danger />
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full py-3 rounded-2xl bg-card border border-border text-sm font-semibold text-foreground"
          dir="auto"
        >
          {t('settings.close')}
        </button>
      </div>
    </div>,
    document.body,
  )
}

function ActionRow({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full px-4 py-3.5 flex items-center gap-3 text-start hover:bg-accent transition-colors border-b border-border/60 last:border-b-0',
        danger ? 'text-red-500' : 'text-foreground',
      )}
      dir="auto"
    >
      <Icon className={cn('w-5 h-5', danger && 'rtl:-scale-x-100')} />
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}

function Bubble({ row, mine, parent, reactions, locale, userId, onTap, onViewImage, onResend, t }) {
  // push_sent === 0 means the push reached zero devices (no
  // subscription, or every endpoint failed). null = legacy/untracked,
  // treated as fine. The message itself is always in the thread; this
  // only flags that the *notification* didn't land.
  const deliveryFailed = row.push_sent === 0
  // A future send the sender queued — only the sender sees it (the
  // hook filters undispatched scheduled rows out for the recipient).
  const pending = row.scheduled_for && !row.dispatched_at
  const time = useMemo(() => {
    try {
      return new Date(row.created_at).toLocaleTimeString(
        locale === 'ar' ? 'ar-EG' : 'en-US',
        { hour: 'numeric', minute: '2-digit' },
      )
    } catch {
      return ''
    }
  }, [row.created_at, locale])

  // Collapse reactions to unique emojis (max two people in this chat,
  // but dedupe defensively).
  const emojis = useMemo(() => {
    const seen = []
    for (const r of reactions) if (!seen.includes(r.emoji)) seen.push(r.emoji)
    return seen
  }, [reactions])

  // Unsent message — a quiet, non-interactive placeholder on both sides.
  if (row.deleted_at) {
    return (
      <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
        <div className="max-w-[78%] rounded-2xl px-3 py-2 bg-muted/50 text-muted-foreground text-[13px] italic inline-flex items-center gap-1.5" dir="auto">
          <Trash2 className="w-3.5 h-3.5" /> {t('messages.thread.deleted')}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
      <button
        type="button"
        onClick={onTap}
        className={cn(
          'max-w-[78%] rounded-2xl px-3 py-2 text-start active:scale-[0.99] transition-transform',
          mine
            ? 'bg-app-primary-500 text-white rounded-ee-sm'
            : 'bg-muted text-foreground rounded-es-sm',
          pending && 'opacity-70 ring-1 ring-dashed ring-white/50',
        )}
      >
        {parent && (
          <div
            className={cn(
              'mb-1 ps-2 py-1 rounded border-s-2 text-[12px] truncate max-w-full',
              mine ? 'border-white/60 bg-white/10' : 'border-app-primary-500 bg-foreground/5',
            )}
            dir="auto"
          >
            <span className="font-semibold">
              {parent.sender_id === userId ? t('messages.thread.you') : ''}
            </span>{' '}
            {parent.body || (parent.image_url ? '📷' : '🎤')}
          </div>
        )}
        {row.title && (
          <p className="text-sm font-bold mb-0.5" dir="auto">{row.title}</p>
        )}
        {/* Image rendering: expired ephemeral → "seen" chip; live
            ephemeral → locked chip (recipient taps to view once/twice,
            sender just sees it's sent); normal → the photo, hardened
            against casual save (no context menu / drag / select).
            NB: screenshots can't be blocked on the web. */}
        {row.image_expired ? (
          <div className="mb-1 flex items-center gap-1.5 rounded-lg bg-foreground/5 px-3 py-2 text-[12px] text-muted-foreground" dir="auto">
            <EyeOff className="w-3.5 h-3.5" /> {t('messages.thread.imageSeen')}
          </div>
        ) : row.image_url && row.image_view_limit ? (
          mine ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewImage?.() }}
              className="mb-1 flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-2 text-[12px]"
              dir="auto"
            >
              <EyeOff className="w-3.5 h-3.5" /> {t('messages.thread.viewOnceSent', { n: row.image_view_limit })}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewImage?.() }}
              className="mb-1 flex items-center gap-1.5 rounded-lg bg-app-primary-500/15 text-app-primary-500 px-3 py-2 text-[12px] font-semibold"
              dir="auto"
            >
              <Eye className="w-3.5 h-3.5" /> {t('messages.thread.tapToView', { n: row.image_view_limit })}
            </button>
          )
        ) : row.image_url ? (
          <img
            src={row.image_url}
            alt=""
            loading="lazy"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onClick={(e) => { e.stopPropagation(); onViewImage?.() }}
            className="rounded-lg mb-1 max-h-64 w-auto object-cover cursor-zoom-in select-none [-webkit-touch-callout:none] [-webkit-user-drag:none]"
          />
        ) : null}
        {row.audio_url && (
          <AudioBubble src={row.audio_url} mine={mine} />
        )}
        {row.body && (
          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed" dir="auto">
            {row.body}
          </p>
        )}
        <div className={cn(
          'flex items-center gap-1 mt-0.5',
          mine ? 'justify-end text-white/70' : 'justify-start text-muted-foreground',
        )}>
          <span className="text-[10px]" dir="auto">
            {pending ? scheduledLabel(row.scheduled_for, locale) : time}
          </span>
          {row.edited_at && (
            <span className="text-[10px] opacity-80" dir="auto">· {t('messages.thread.edited')}</span>
          )}
          {mine && (
            row._pending
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-label={t('messages.thread.sent')} />
              : row._failed
                ? <AlertCircle className="w-3.5 h-3.5 text-amber-200" aria-label={t('messages.thread.notDelivered')} />
                : pending
                  ? <Clock className="w-3.5 h-3.5" aria-label={t('messages.thread.scheduled')} />
                  : row.read_at
                    ? <CheckCheck className="w-3.5 h-3.5" aria-label={t('messages.thread.read')} />
                    : deliveryFailed
                      ? <AlertCircle className="w-3.5 h-3.5 text-amber-200" aria-label={t('messages.thread.notDelivered')} />
                      : <Check className="w-3.5 h-3.5" aria-label={t('messages.thread.sent')} />
          )}
        </div>
      </button>
      {emojis.length > 0 && (
        <div className={cn('flex gap-0.5 -mt-1.5 z-10', mine ? 'me-2' : 'ms-2')}>
          {emojis.map((e) => (
            <span
              key={e}
              className="text-xs bg-card border border-border rounded-full px-1.5 py-0.5 shadow-sm"
            >
              {e}
            </span>
          ))}
        </div>
      )}
      {mine && deliveryFailed && !row.read_at && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onResend?.() }}
          className="mt-0.5 me-1 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 hover:underline"
          dir="auto"
        >
          <RotateCw className="w-3 h-3" />
          {t('messages.thread.notDeliveredRetry')}
        </button>
      )}
    </div>
  )
}

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

// Compact voice-note player. Tap toggles play/pause on a single
// <audio>; a progress bar + time track playback. stopPropagation on
// the controls so tapping play doesn't open the bubble action sheet.
function AudioBubble({ src, mine }) {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)

  const toggle = (e) => {
    e.stopPropagation()
    const a = audioRef.current
    if (!a) return
    if (a.paused) { a.play(); setPlaying(true) }
    else { a.pause(); setPlaying(false) }
  }

  return (
    <div
      className="flex items-center gap-2 min-w-[160px] py-0.5"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          mine ? 'bg-white/20 text-white' : 'bg-app-primary-500/15 text-app-primary-500',
        )}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 rtl:-scale-x-100" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={cn('h-1 rounded-full overflow-hidden', mine ? 'bg-white/30' : 'bg-foreground/15')}>
          <div
            className={cn('h-full', mine ? 'bg-white' : 'bg-app-primary-500')}
            style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
          />
        </div>
      </div>
      <span className={cn('text-[10px] tabular-nums flex-shrink-0', mine ? 'text-white/80' : 'text-muted-foreground')} dir="ltr">
        {formatDuration(progress || duration)}
      </span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(Number.isFinite(e.target.duration) ? e.target.duration : 0)}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onEnded={() => { setPlaying(false); setProgress(0) }}
        className="hidden"
      />
    </div>
  )
}

// Short "queued for …" label on a pending scheduled bubble.
function scheduledLabel(iso, locale) {
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    const opts = sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    return d.toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US', opts)
  } catch {
    return ''
  }
}

// Flatten the message list into render items, injecting a date
// divider whenever the calendar day changes.
function groupByDay(messages, locale) {
  const out = []
  let lastDay = null
  for (const row of messages) {
    const d = new Date(row.created_at)
    const dayKey = d.toDateString()
    if (dayKey !== lastDay) {
      lastDay = dayKey
      out.push({ type: 'divider', key: `d-${dayKey}`, label: formatDayLabel(d, locale) })
    }
    out.push({ type: 'row', row })
  }
  return out
}

function formatDayLabel(d, locale) {
  const now = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now.getTime() - 86400000).toDateString()
  if (d.toDateString() === today) return locale === 'ar' ? 'النهارده' : 'Today'
  if (d.toDateString() === yesterday) return locale === 'ar' ? 'إمبارح' : 'Yesterday'
  try {
    return d.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', { dateStyle: 'medium' })
  } catch {
    return d.toDateString()
  }
}

export default MessagesPage
